import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { EnterprisePageHeader } from '@/components/enterprise';
import {
  goodsReceiptNoteService,
  type CriticalMtcGapDTO,
  type CriticalMtcGapFilters,
  type CriticalMtcGapReason,
} from '@/services/goodsReceiptNote.service';
import { useAuthStore } from '@/store/authStore';
import { formatDate } from '@/utils/format/date';
import { formatQuantity } from '@/utils/format/quantity';

const { Text } = Typography;
const { Search } = Input;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

type HeatDraft = {
  key: string;
  heat_number: string;
  dimension: string;
  quantity: number;
  mtc_document_id: string | null;
};

function canEditGrn(permissions?: string[]) {
  if (!permissions?.length) return true;
  return (
    permissions.includes('procurement.grn.edit') ||
    permissions.includes('procurement.po.edit') ||
    permissions.includes('procurement.admin')
  );
}

export const CriticalMtcGapsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const permissions = useAuthStore((s) => s.user?.permissions);
  const canRemediate = canEditGrn(permissions);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [gapFilter, setGapFilter] = useState<CriticalMtcGapReason | undefined>(undefined);

  const [attachRow, setAttachRow] = useState<CriticalMtcGapDTO | null>(null);
  const [attachMtcRef, setAttachMtcRef] = useState('');

  const [heatRow, setHeatRow] = useState<CriticalMtcGapDTO | null>(null);
  const [heatDrafts, setHeatDrafts] = useState<HeatDraft[]>([]);

  const filters: CriticalMtcGapFilters = {
    search: searchText || undefined,
    status: statusFilter,
    gap_reason: gapFilter,
  };

  const { data: gaps = [], isLoading } = useQuery({
    queryKey: ['critical-mtc-gaps', filters],
    queryFn: () => goodsReceiptNoteService.listCriticalMtcGaps(filters),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['critical-mtc-gaps'] });
  };

  const attachMutation = useMutation({
    mutationFn: async () => {
      if (!attachRow?.heat_line_id) throw new Error('No heat line');
      const ref = attachMtcRef.trim();
      if (!ref) throw new Error('MTC document is required');
      return goodsReceiptNoteService.setHeatLineMtc(attachRow.grn_id, attachRow.heat_line_id, ref);
    },
    onSuccess: () => {
      message.success('MTC attached');
      setAttachRow(null);
      setAttachMtcRef('');
      invalidate();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Failed to attach MTC');
    },
  });

  const heatMutation = useMutation({
    mutationFn: async () => {
      if (!heatRow) throw new Error('No row');
      const heat_lines = heatDrafts
        .filter((h) => Number(h.quantity) > 0)
        .map((h) => ({
          heat_number: h.heat_number.trim(),
          dimension: h.dimension.trim(),
          quantity: Number(h.quantity),
          mtc_document_id: h.mtc_document_id?.trim() || null,
        }));
      if (!heat_lines.length) throw new Error('Add at least one heat line');
      if (heatRow.mtc_required_on_receipt && heat_lines.some((h) => !h.mtc_document_id)) {
        throw new Error('MTC is required on each heat line for this item');
      }
      return goodsReceiptNoteService.addRemediationHeatLines(
        heatRow.grn_id,
        heatRow.grn_item_id,
        heat_lines
      );
    },
    onSuccess: () => {
      message.success('Heat lines saved');
      setHeatRow(null);
      setHeatDrafts([]);
      invalidate();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Failed to add heat lines');
    },
  });

  const openHeatModal = (row: CriticalMtcGapDTO) => {
    setHeatRow(row);
    setHeatDrafts([
      {
        key: '1',
        heat_number: '',
        dimension: '',
        quantity: Number(row.accepted_quantity) || 0,
        mtc_document_id: null,
      },
    ]);
  };

  const heatQtySum = useMemo(
    () => heatDrafts.reduce((s, h) => s + (Number(h.quantity) || 0), 0),
    [heatDrafts]
  );

  const columns: ColumnsType<CriticalMtcGapDTO> = [
    {
      title: 'GRN',
      dataIndex: 'grn_number',
      width: 160,
      render: (num: string, row) => (
        <Button
          type="link"
          size="small"
          style={{ paddingInline: 0 }}
          onClick={() => navigate(`/scm/procurement/grn/${row.grn_id}`)}
        >
          {num}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'grn_status',
      width: 100,
      render: (s: string) => <Tag color={statusColors[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'grn_date',
      width: 110,
      render: (d: string) => formatDate(d) || '—',
    },
    {
      title: 'PO',
      dataIndex: 'po_number',
      width: 140,
      render: (v: string | null) => v || '—',
    },
    { title: 'Line', dataIndex: 'line_number', width: 60, align: 'right' },
    {
      title: 'Item',
      dataIndex: 'item_code',
      width: 130,
      render: (code: string | null, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{code || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {row.item_description}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'item_type',
      width: 100,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Accepted',
      dataIndex: 'accepted_quantity',
      width: 90,
      align: 'right',
      render: (v: number) => formatQuantity(v),
    },
    {
      title: 'Heat',
      key: 'heat',
      width: 140,
      render: (_: unknown, row) =>
        row.heat_line_id ? (
          <Space direction="vertical" size={0}>
            <Text>{row.heat_number}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.dimension} · qty {formatQuantity(row.heat_quantity)}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">None</Text>
        ),
    },
    {
      title: 'Gap',
      dataIndex: 'gap_reason',
      width: 170,
      render: (r: CriticalMtcGapReason, row) =>
        r === 'missing_heat_lines' ? (
          <Tag color="orange">
            {row.mtc_required_on_receipt ? 'Missing heat + MTC' : 'Missing heat lines'}
          </Tag>
        ) : (
          <Tag color="red">Missing MTC</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_: unknown, row) => {
        if (!canRemediate) {
          return (
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/scm/procurement/grn/${row.grn_id}`)}
            >
              View GRN
            </Button>
          );
        }
        if (row.gap_reason === 'missing_heat_lines') {
          return (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openHeatModal(row)}>
              Add heat + MTC
            </Button>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => {
              setAttachRow(row);
              setAttachMtcRef('');
            }}
          >
            Attach MTC
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <EnterprisePageHeader
        documentLabel="Critical MTC gaps"
        subtitle="Review critical GRN lines missing heat numbers or Mill Test Certificates, including Posted receipts."
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Remediation does not reopen or re-post the GRN. Attaching MTC or adding heat lines only updates the compliance record. Lines with no heat splits yet appear as “Missing heat + MTC” (and under the Missing MTC filter) because an MTC can only be attached to a heat line."
      />

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Search
            allowClear
            placeholder="Search GRN, PO, item…"
            onSearch={setSearchText}
            onChange={(e) => {
              if (!e.target.value) setSearchText('');
            }}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            placeholder="GRN status"
            style={{ width: 140 }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={['Draft', 'Submitted', 'Approved', 'Posted'].map((s) => ({
              value: s,
              label: s,
            }))}
          />
          <Select
            allowClear
            placeholder="Gap type"
            style={{ width: 200 }}
            value={gapFilter}
            onChange={(v) => setGapFilter(v)}
            options={[
              { value: 'missing_heat_lines', label: 'Missing heat lines' },
              { value: 'missing_mtc', label: 'Missing MTC (incl. no heats)' },
            ]}
          />
        </Space>

        <Table
          rowKey={(r) =>
            r.heat_line_id
              ? `${r.grn_item_id}-${r.heat_line_id}`
              : `${r.grn_item_id}-no-heat`
          }
          loading={isLoading}
          dataSource={gaps}
          columns={columns}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={
          attachRow
            ? `Attach MTC — ${attachRow.grn_number} / ${attachRow.item_code} / heat ${attachRow.heat_number}`
            : 'Attach MTC'
        }
        open={!!attachRow}
        onCancel={() => {
          setAttachRow(null);
          setAttachMtcRef('');
        }}
        onOk={() => attachMutation.mutate()}
        confirmLoading={attachMutation.isPending}
        okText="Save MTC"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text type="secondary">Document number or UUID</Text>
            <Input
              value={attachMtcRef}
              onChange={(e) => setAttachMtcRef(e.target.value)}
              placeholder="Upload below or paste document number / UUID"
            />
          </div>
          {attachRow?.heat_line_id && (
            <DocumentUpload
              entityType="GrnItemHeatLine"
              entityId={attachRow.heat_line_id}
              buttonText="Upload MTC"
              onSuccess={(doc) => setAttachMtcRef(doc.id)}
            />
          )}
        </Space>
      </Modal>

      <Modal
        title={
          heatRow
            ? `Add heat lines — ${heatRow.grn_number} / ${heatRow.item_code} (accepted ${heatRow.accepted_quantity})`
            : 'Add heat lines'
        }
        open={!!heatRow}
        onCancel={() => {
          setHeatRow(null);
          setHeatDrafts([]);
        }}
        onOk={() => heatMutation.mutate()}
        confirmLoading={heatMutation.isPending}
        okText="Save heat lines"
        width={720}
        destroyOnClose
      >
        {heatRow && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Quantities must sum to accepted ({heatRow.accepted_quantity}). Current sum:{' '}
              <Text strong type={Math.abs(heatQtySum - Number(heatRow.accepted_quantity)) > 0.0001 ? 'danger' : undefined}>
                {heatQtySum}
              </Text>
            </Text>
            {heatDrafts.map((row, idx) => (
              <Card key={row.key} size="small" title={`Heat line ${idx + 1}`}>
                <Space wrap style={{ width: '100%' }}>
                  <Input
                    placeholder="Heat number"
                    style={{ width: 140 }}
                    value={row.heat_number}
                    onChange={(e) => {
                      const v = e.target.value;
                      setHeatDrafts((prev) =>
                        prev.map((h) => (h.key === row.key ? { ...h, heat_number: v } : h))
                      );
                    }}
                  />
                  <Input
                    placeholder="Dimension"
                    style={{ width: 140 }}
                    value={row.dimension}
                    onChange={(e) => {
                      const v = e.target.value;
                      setHeatDrafts((prev) =>
                        prev.map((h) => (h.key === row.key ? { ...h, dimension: v } : h))
                      );
                    }}
                  />
                  <InputNumber
                    min={0}
                    style={{ width: 110 }}
                    value={row.quantity}
                    onChange={(v) => {
                      setHeatDrafts((prev) =>
                        prev.map((h) =>
                          h.key === row.key ? { ...h, quantity: Number(v) || 0 } : h
                        )
                      );
                    }}
                  />
                  <Input
                    placeholder="MTC doc # / UUID"
                    style={{ width: 180 }}
                    value={row.mtc_document_id ?? ''}
                    onChange={(e) => {
                      const v = e.target.value.trim() || null;
                      setHeatDrafts((prev) =>
                        prev.map((h) => (h.key === row.key ? { ...h, mtc_document_id: v } : h))
                      );
                    }}
                  />
                  <DocumentUpload
                    entityType="GoodsReceiptNote"
                    entityId={heatRow.grn_id}
                    buttonText="Upload MTC"
                    onSuccess={(doc) => {
                      setHeatDrafts((prev) =>
                        prev.map((h) =>
                          h.key === row.key ? { ...h, mtc_document_id: doc.id } : h
                        )
                      );
                    }}
                  />
                  {heatDrafts.length > 1 && (
                    <Button
                      danger
                      type="link"
                      onClick={() =>
                        setHeatDrafts((prev) => prev.filter((h) => h.key !== row.key))
                      }
                    >
                      Remove
                    </Button>
                  )}
                </Space>
              </Card>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() =>
                setHeatDrafts((prev) => [
                  ...prev,
                  {
                    key: String(Date.now()),
                    heat_number: '',
                    dimension: '',
                    quantity: 0,
                    mtc_document_id: null,
                  },
                ])
              }
            >
              Add another heat line
            </Button>
          </Space>
        )}
      </Modal>

      <div style={{ marginTop: 12 }}>
        <Link to="/scm/procurement/grn">Back to Goods Receipt Notes</Link>
      </div>
    </div>
  );
};

export default CriticalMtcGapsPage;
